import axios from 'axios'
import { Message, MessageBox } from 'element-ui'
import store from '@/store'
import router from '@/router'
import { getToken } from '@/utils/auth'
import { 
  handleResponse, 
  handleError, 
  isSuccessResponse, 
  ErrorCodes,
  getErrorMessage,
  showErrorMessage,
  smartRetry,
  logError,
  checkNetworkStatus
} from '@/models/api'
import CryptoJS from 'crypto-js'

const permissionCriticalUrls = ['/user/info']

function normalizeRequestUrl(url) {
  if (!url) return ''
  const plainUrl = String(url).split('?')[0]
  return plainUrl.replace(/^\/api/, '')
}

function isPermissionCriticalRequest(config = {}) {
  if (config.permissionCritical === true) return true
  const currentUrl = normalizeRequestUrl(config.url)
  return permissionCriticalUrls.some(url => currentUrl === url || currentUrl.startsWith(`${url}/`))
}

function shouldShow403Toast(config = {}) {
  if (config.permissionToast === false) return false
  if (config.permissionToast === true) return true

  const method = String(config.method || 'get').toLowerCase()
  return !['get', 'head', 'options'].includes(method)
}

function handle403ByCategory(apiError, requestConfig = {}) {
  const isCritical = isPermissionCriticalRequest(requestConfig)
  if (isCritical && getToken()) {
    const currentPath = router.currentRoute.path
    if (currentPath !== '/403' && currentPath !== '/login') {
      router.replace('/403').catch(() => {})
    }
    return
  }

  if (!shouldShow403Toast(requestConfig)) {
    return
  }

  const message = apiError?.msg || apiError?.message || '当前操作无权限'
  Message.warning(message)
}

// 创建axios实例
const isDev = process.env.NODE_ENV === 'development'

const service = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/api',
  timeout: 15000
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    const token = getToken()
    
    if (token) {
      // 让每个请求携带token
      config.headers['Authorization'] = 'Bearer ' + token
    }
    
    // 开发环境下暂时禁用加密以便调试
    // 加密敏感请求数据
    // if (config.method === 'post' || config.method === 'put' || config.method === 'patch') {
    //   config.data = encryptRequestData(config.data)
    // }
    
    const maskedHeaders = { ...(config.headers || {}) }
    if (maskedHeaders.Authorization) {
      maskedHeaders.Authorization = 'Bearer ***'
    }

    if (isDev) {
      console.debug('[Request]', config.url, config.method, config.params || config.data || '')
    }
    
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  /**
   * 如果您想获取http信息，如headers或status
   * 请返回 response => response
  */

  /**
   * 通过自定义代码确定请求状态
   * 这只是一个例子
   * 您还可以通过HTTP状态码判断状态
   */
  response => {
    if (response.config.responseType === 'blob') {
      // 检查是否是 JSON 类型的错误响应
      if (response.data.type === 'application/json') {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            try {
              const res = JSON.parse(reader.result)
              const apiResponse = handleResponse(res)
              showErrorMessage(apiResponse, Message, null)
              reject(apiResponse)
            } catch (err) {
              reject(new Error('解析错误信息失败'))
            }
          }
          reader.onerror = () => reject(new Error('读取错误信息失败'))
          reader.readAsText(response.data)
        })
      }
      return response.data
    }
    
    const res = response.data
    
    // 开发环境下暂时禁用解密以便调试
    // try {
    //   // 解密响应数据
    //   res.data = decryptResponseData(res.data)
    // } catch (decryptError) {
    //   console.error('响应数据解密失败:', decryptError)
    //   throw new Error('响应数据解密失败')
    // }
  
    const apiResponse = handleResponse(res)
  
    if (apiResponse.isError()) {
      if (apiResponse.code === 403) {
        const msg = apiResponse.msg || apiResponse.message || '权限不足，无法访问该资源'
        if (shouldShow403Toast(response.config)) {
          Message.warning(msg)
        }
        handle403ByCategory(apiResponse, response.config)
        return Promise.reject(apiResponse)
      }

      // 使用增强的错误处理和显示
      const handledError = showErrorMessage(apiResponse, Message, null)
      
      // 记录错误日志
      logError(apiResponse, {
        url: response.config.url,
        method: response.config.method
      })
  
      // 特殊错误码处理 (20001-20008认证相关错误)
      if (apiResponse.needReLogin()) {
        MessageBox.confirm('您已注销，可以取消以停留在此页面，或重新登录', '确认退出', {
          confirmButtonText: '重新登录',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          store.dispatch('user/clearLoginInfo').then(() => {
            location.reload()
          })
        }).catch(() => {
          // 用户选择取消，无需处理
        })
      }
  
      return Promise.reject(handledError)
    } else {
      return apiResponse.data
    }
  },
  error => {
    if (isDev) {
      console.debug('[Response Error]', error.config?.url, error.message)
    }
    
    // 特殊处理 Blob 类型的错误响应
    if (error.response && error.response.data instanceof Blob && error.response.data.type === 'application/json') {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          try {
            const res = JSON.parse(reader.result)
            const apiResponse = handleResponse(res)
            showErrorMessage(apiResponse, Message, null)
            reject(apiResponse)
          } catch (err) {
            const apiError = handleError(error)
            showErrorMessage(apiError, Message, null)
            reject(apiError)
          }
        }
        reader.onerror = () => {
          const apiError = handleError(error)
          showErrorMessage(apiError, Message, null)
          reject(apiError)
        }
        reader.readAsText(error.response.data)
      })
    }
    
    // 使用新的错误处理模型
    const apiError = handleError(error)
    
    // 使用增强的错误显示
    if (apiError.code === 403) {
      handle403ByCategory(apiError, error.config)
      return Promise.reject(apiError)
    }

    const handledError = showErrorMessage(apiError, Message, null)
    
    // 记录错误日志
    logError(apiError, {
      url: error.config?.url,
      method: error.config?.method
    })
    
    // 检查网络状态，提供更友好的错误提示
    if (apiError.isNetworkError()) {
      checkNetworkStatus().then(isOnline => {
        if (!isOnline) {
          Message({
            message: '网络连接异常，请检查网络设置',
            type: 'warning',
            duration: 7000
          })
        }
      })
    }
    
    return Promise.reject(handledError)
  }
)

// 加密请求数据
function encryptRequestData(data) {
  if (!data || typeof data !== 'object') {
    return data
  }
  
  // 需要加密的敏感字段列表
  const sensitiveFields = ['password', 'token', 'secret', 'key', 'creditCard', 'idCard']
  
  // 深拷贝数据，避免修改原始数据
  const encryptedData = JSON.parse(JSON.stringify(data))
  
  // 遍历对象，加密敏感字段
  Object.keys(encryptedData).forEach(key => {
    if (sensitiveFields.includes(key) && encryptedData[key]) {
      encryptedData[key] = encrypt(encryptedData[key])
    } else if (typeof encryptedData[key] === 'object' && encryptedData[key] !== null) {
      // 递归处理嵌套对象
      encryptedData[key] = encryptRequestData(encryptedData[key])
    }
  })
  
  return encryptedData
}

// 解密响应数据
function decryptResponseData(data) {
  if (!data || typeof data !== 'object') {
    return data
  }
  
  // 深拷贝数据，避免修改原始数据
  const decryptedData = JSON.parse(JSON.stringify(data))
  
  // 遍历对象，解密所有加密字段（假设加密字段都以_encrypted结尾）
  Object.keys(decryptedData).forEach(key => {
    if (key.endsWith('_encrypted') && decryptedData[key]) {
      // 解密后使用原字段名（去掉_encrypted后缀）
      const originalKey = key.replace('_encrypted', '')
      decryptedData[originalKey] = decrypt(decryptedData[key])
      delete decryptedData[key]
    } else if (typeof decryptedData[key] === 'object' && decryptedData[key] !== null) {
      // 递归处理嵌套对象
      decryptedData[key] = decryptResponseData(decryptedData[key])
    }
  })
  
  return decryptedData
}

// 加密单个字段
function encrypt(text) {
  if (!text) return ''
  try {
    const key = CryptoJS.enc.Utf8.parse('your-encryption-key-16') // 16位密钥
    const iv = CryptoJS.enc.Utf8.parse('your-initialization-vector-16') // 16位初始化向量
    const encrypted = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(text),
      key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    )
    return encrypted.toString()
  } catch (error) {
    if (isDev) console.debug('[Encrypt Error]')
    return text
  }
}

// 解密单个字段
function decrypt(ciphertext) {
  if (!ciphertext) return ''
  try {
    const key = CryptoJS.enc.Utf8.parse('your-encryption-key-16') // 16位密钥
    const iv = CryptoJS.enc.Utf8.parse('your-initialization-vector-16') // 16位初始化向量
    const decrypted = CryptoJS.AES.decrypt(
      ciphertext,
      key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    )
    return decrypted.toString(CryptoJS.enc.Utf8)
  } catch (error) {
    if (isDev) console.debug('[Decrypt Error]')
    return ciphertext
  }
}

export default service

/**
 * 包装axios请求，添加智能重试机制
 * @param {Object} config - axios配置
 * @param {Object} retryOptions - 重试选项
 * @returns {Promise}
 */
export function requestWithRetry(config, retryOptions = {}) {
  return smartRetry(() => service(config), retryOptions)
}

/**
 * GET请求封装（带重试）
 * @param {string} url - 请求URL
 * @param {Object} config - axios配置
 * @param {Object} retryOptions - 重试选项
 * @returns {Promise}
 */
export function get(url, config = {}, retryOptions = {}) {
  return requestWithRetry({
    url,
    method: 'get',
    ...config
  }, retryOptions)
}

/**
 * POST请求封装（带重试）
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {Object} config - axios配置
 * @param {Object} retryOptions - 重试选项
 * @returns {Promise}
 */
export function post(url, data = {}, config = {}, retryOptions = {}) {
  return requestWithRetry({
    url,
    method: 'post',
    data,
    ...config
  }, retryOptions)
}

/**
 * PUT请求封装（带重试）
 * @param {string} url - 请求URL
 * @param {Object} data - 请求数据
 * @param {Object} config - axios配置
 * @param {Object} retryOptions - 重试选项
 * @returns {Promise}
 */
export function put(url, data = {}, config = {}, retryOptions = {}) {
  return requestWithRetry({
    url,
    method: 'put',
    data,
    ...config
  }, retryOptions)
}

/**
 * DELETE请求封装（带重试）
 * @param {string} url - 请求URL
 * @param {Object} config - axios配置
 * @param {Object} retryOptions - 重试选项
 * @returns {Promise}
 */
export function del(url, config = {}, retryOptions = {}) {
  return requestWithRetry({
    url,
    method: 'delete',
    ...config
  }, retryOptions)
}
